import { type Key, type ReactNode } from 'react'

// Generic, accessible HTML <table> with a sticky header, an empty
// state, and a footer slot for pagination. We deliberately avoid
// virtualization in this iteration — every operator queue is small and
// browser semantics (Ctrl+F, native text selection, copy/paste,
// screen-reader headers) are worth more than a few extra DOM nodes.

export interface ColumnConfig<T> {
  key: string
  header: ReactNode
  // Optional ARIA / Tailwind hooks for individual cells.
  align?: 'left' | 'right' | 'center'
  className?: string
  headerClassName?: string
  // The cell body. Receives the row plus the row index for fallback
  // keying when rows lack a stable identifier.
  cell: (row: T, index: number) => ReactNode
}

interface DataTableProps<T> {
  columns: ColumnConfig<T>[]
  rows: T[]
  // Stable React key for each row. Falls back to the index when the
  // caller passes a non-string identifier they prefer not to expose.
  rowKey: (row: T, index: number) => Key
  // Caption is announced by screen readers as the table's accessible
  // name. Pass it for every list view so the page is navigable.
  caption: string
  // Optional row click handler (whole row becomes a button-like
  // affordance with proper ARIA semantics).
  onRowClick?: (row: T) => void
  isLoading?: boolean
  loadingMessage?: ReactNode
  isError?: boolean
  errorMessage?: ReactNode
  emptyTitle?: ReactNode
  emptyDescription?: ReactNode
  footer?: ReactNode
  className?: string
}

const ALIGN_CLASSES: Record<NonNullable<ColumnConfig<unknown>['align']>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  onRowClick,
  isLoading,
  loadingMessage,
  isError,
  errorMessage,
  emptyTitle = 'No rows',
  emptyDescription,
  footer,
  className,
}: DataTableProps<T>) {
  const colCount = columns.length || 1

  const cls = [
    'overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div className={cls}>
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full divide-y divide-stone-200 text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="sticky top-0 z-10 bg-stone-50 text-xs uppercase
                            tracking-wide text-stone-600">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={[
                    'px-3 py-2 font-medium',
                    ALIGN_CLASSES[column.align ?? 'left'],
                    column.headerClassName,
                  ].filter(Boolean).join(' ')}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {isLoading ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-3 py-6 text-center text-sm text-stone-500"
                >
                  {loadingMessage ?? 'Loading…'}
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-3 py-6 text-center text-sm text-red-700"
                >
                  {errorMessage ?? 'Failed to load data.'}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-3 py-10 text-center"
                >
                  <div className="text-sm font-medium text-stone-700">
                    {emptyTitle}
                  </div>
                  {emptyDescription && (
                    <div className="mt-1 text-xs text-stone-500">
                      {emptyDescription}
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const key = rowKey(row, index)
                const onClick = onRowClick ? () => onRowClick(row) : undefined
                return (
                  <tr
                    key={key}
                    onClick={onClick}
                    onKeyDown={(event) => {
                      if (!onClick) return
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onClick()
                      }
                    }}
                    role={onClick ? 'button' : undefined}
                    tabIndex={onClick ? 0 : undefined}
                    className={[
                      'group',
                      onClick
                        ? 'cursor-pointer hover:bg-accent/5 ' +
                          'focus:outline-none focus-visible:bg-accent/10'
                        : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={[
                          'px-3 py-2 align-top text-stone-800',
                          ALIGN_CLASSES[column.align ?? 'left'],
                          column.className,
                        ].filter(Boolean).join(' ')}
                      >
                        {column.cell(row, index)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      {footer && <div>{footer}</div>}
    </div>
  )
}
